import React from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useGate, useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {localizeTitle} from '@entities/catalogs/lib';
import {ViolationTypesGate, violationTypesModel} from '@entities/catalogs/model';
import {ViolationTypeDto} from '@entities/catalogs/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Icon} from '@shared/ui/Icon';
import {Input} from '@shared/ui/Input';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {Textarea} from '@shared/ui/Textarea';

import {FieldError, RowActions, Wrapper} from './styled';

type ViolationTypeFormValues = {
    title: string;
    description: string;
};

const requiredTrimmed = (message: string) =>
    yup
        .string()
        .transform((value) => String(value ?? '').trim())
        .required(message)
        .test('not-blank', message, (value) => String(value ?? '').trim().length > 0);

const schema = (message: string) =>
    yup.object().shape({
        title: requiredTrimmed(message),
        description: requiredTrimmed(message),
    });

export const ViolationTypesTab = () => {
    const {t, i18n} = useTranslation();
    const model = useUnit(violationTypesModel);
    const [editId, setEditId] = React.useState<string | null>(null);

    useGate(ViolationTypesGate);

    const {control, handleSubmit, errors, reset} = useForm<ViolationTypeFormValues>({
        resolver: yupResolver(schema(t('page_audit.page_catalogs.validation.select-category'))),
        mode: 'all',
        defaultValues: {title: '', description: ''},
    });

    const startEdit = React.useCallback(
        (row: ViolationTypeDto) => {
            reset({
                title: localizeTitle(row.title, i18n.language),
                description: localizeTitle(row.description, i18n.language),
            });
            setEditId(row.id);
        },
        [i18n.language, reset],
    );

    const cancel = React.useCallback(() => {
        setEditId(null);
        reset({title: '', description: ''});
    }, [reset]);

    const onSubmit = React.useCallback(
        (values: ViolationTypeFormValues) => {
            const row = model.state.data.find((item) => item.id === editId);
            if (!row || !editId) return;

            model.save({
                id: editId,
                payload: {
                    title: {
                        ...(row.title ?? {}),
                        [i18n.language]: values.title.trim(),
                    },
                    description: {
                        ...(row.description ?? {}),
                        [i18n.language]: values.description.trim(),
                    },
                    parent: row.parent ? {id: row.parent.id} : null,
                    root: row.root ? {id: row.root.id} : null,
                },
            });
            cancel();
        },
        [cancel, editId, i18n.language, model],
    );

    const data = React.useMemo(
        () =>
            model.state.data
                .slice()
                .sort((a, b) =>
                    localizeTitle(a.title, i18n.language).localeCompare(
                        localizeTitle(b.title, i18n.language),
                    ),
                )
                .map((row) => {
                    const isEditable = editId === row.id;

                    return {
                        ...row,
                        title: isEditable ? (
                            <>
                                <Controller
                                    name="title"
                                    control={control}
                                    render={({onChange, value, name}) => (
                                        <Input
                                            size="sm"
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                            hasError={Boolean(errors.title)}
                                            disabled={model.state.pending}
                                            placeholder={t('page_audit.terms.type')}
                                        />
                                    )}
                                />
                                {errors.title && <FieldError>{errors.title.message}</FieldError>}
                            </>
                        ) : (
                            localizeTitle(row.title, i18n.language)
                        ),
                        description: isEditable ? (
                            <>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({onChange, value, name}) => (
                                        <Textarea
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                            rows={4}
                                            style={{width: 250, height: 42}}
                                            hasError={Boolean(errors.description)}
                                            disabled={model.state.pending}
                                            placeholder={t('page_audit.terms.description')}
                                        />
                                    )}
                                />
                                {errors.description && (
                                    <FieldError style={{marginLeft: 8}}>
                                        {errors.description.message}
                                    </FieldError>
                                )}
                            </>
                        ) : (
                            localizeTitle(row.description, i18n.language)
                        ),
                        actions: (
                            <RowActions>
                                {isEditable ? (
                                    <>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={ButtonVariant.Outline}
                                            disabled={model.state.pending}
                                            onClick={cancel}
                                        >
                                            {t('actions.cancel')}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            disabled={model.state.pending}
                                            onClick={handleSubmit(onSubmit)}
                                        >
                                            {t('actions.save')}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={ButtonVariant.Text}
                                        disabled={model.state.pending}
                                        onClick={() => startEdit(row)}
                                    >
                                        <Icon name="settings" width={16} color="#4F79D0" />
                                    </Button>
                                )}
                            </RowActions>
                        ),
                    };
                }),
        [
            cancel,
            control,
            editId,
            errors.description,
            errors.title,
            i18n.language,
            model,
            startEdit,
            t,
        ],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns([
                {id: 'title', header: t('page_audit.terms.type'), minWidth: 350},
                {id: 'description', header: t('page_audit.terms.description'), minWidth: 350},
                {id: 'actions', header: '', width: 120},
            ]),
        [t],
    );

    return (
        <Wrapper>
            <form id="violation-type-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <Table
                    columns={columns}
                    data={data}
                    isLoading={model.state.pending}
                    hasFooter={false}
                />
            </form>
        </Wrapper>
    );
};
